import React, { ReactNode } from 'react';
import { TenantProvider } from './TenantProvider';
import { SiteConfigProvider } from './SiteConfigProvider';
import { FavoritesProvider } from './FavoritesProvider';
import { useTenantContext } from './TenantProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Criar QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutos
      refetchOnWindowFocus: false,
    },
  },
});

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Wrapper interno que usa o tenant context
 */
function SiteConfigWrapper({ children }: { children: ReactNode }) {
  const { subdominio } = useTenantContext();
  
  return (
    <SiteConfigProvider subdominio={subdominio}>
      <FavoritesProvider>
        {children}
      </FavoritesProvider>
    </SiteConfigProvider>
  );
}

/**
 * Provider centralizado que agrupa todos os providers da aplicação
 * 
 * Ordem de providers:
 * 1. QueryClientProvider - React Query
 * 2. TenantProvider - Detecção de tenant (subdomínio)
 * 3. SiteConfigProvider - Configurações do site baseadas no tenant
 * 4. FavoritesProvider - Gerenciamento de favoritos
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <SiteConfigWrapper>
          {children}
        </SiteConfigWrapper>
      </TenantProvider>
    </QueryClientProvider>
  );
}

/**
 * Hook para acessar todos os contexts da aplicação
 */
export function useApp() {
  const tenant = useTenantContext();
  
  return {
    tenant,
  };
}
